<template>
  <Layout>
    <McLoading :isLoading="isTransitionLoading" />
    <div class="mc-delivery">
      <div :class="['mc-list-header', { 'mc-list-header--sticky': isStickyHeader }]">
        <PageHeader :title="title" :subTitle="subTitle" :isSticky="isStickyHeader">
          <template v-if="!isMobileHeader" #afterTitle>
            <div class="d-flex gap-2 delivery-header-actions">
              <McListDownloader
                class="delivery-header-actions__download"
                :pageOptions="downloadPageOptions"
                :fileFormatOptions="['excel']"
                :buttonProps="downloadButtonProps"
                @download="handleDownload"
              />
              <McButton
                class="delivery-header-actions__upload"
                variant="secondary"
                :text="$t('global.action.upload')"
                icon="icon-outline_upload"
                :screenBreakpoint="buttonScreenBreakpoint"
                tooltipPosition="bottom"
                @click="$router.push({ name: 'Delivery Import' })"
              />
              <b-button-group class="delivery-header-actions__add-group">
                <McButton
                  variant="primary"
                  :text="$t('global.addField')"
                  :class="{ '__button-group': dropdownItems.length > 0 }"
                  icon="icon-fill_plus"
                  @click="handleAdd"
                />
                <template v-if="dropdownItems.length > 0">
                  <div class="mc-border"></div>
                  <b-dropdown
                    class="mc-buttons-dropdown delivery-header-actions__add-dropdown"
                    variant="primary"
                  >
                    <template #button-content>
                      <i class="mc-icon icon-outline_chevron-down" style="font-size: 18px" />
                    </template>

                    <b-dropdown-item
                      v-for="item in dropdownItems"
                      :key="item.key"
                      @click="
                        item.routeName ? $router.push({ name: item.routeName }) : item.action()
                      "
                    >
                      <div class="d-flex justify-content-between">
                        <div class="me-4">
                          {{ item.label }}
                        </div>
                        <img
                          v-if="item.key == 'accurate'"
                          class="icon-provider"
                          :src="require('@/assets/images/setting/integration/accurate.png')"
                        />
                      </div>
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-button-group>
            </div>
          </template>
          <template v-if="isMobileHeader" #footer>
            <div class="d-flex gap-2 delivery-header-actions delivery-header-actions--mobile">
              <McListDownloader
                class="delivery-header-actions__download"
                :pageOptions="downloadPageOptions"
                :fileFormatOptions="['excel']"
                :buttonProps="downloadButtonProps"
                @download="handleDownload"
              />
              <McButton
                class="delivery-header-actions__upload"
                variant="secondary"
                :text="$t('global.action.upload')"
                icon="icon-outline_upload"
                :screenBreakpoint="buttonScreenBreakpoint"
                tooltipPosition="bottom"
                @click="$router.push({ name: 'Delivery Import' })"
              />
              <b-button-group class="delivery-header-actions__add-group">
                <McButton
                  variant="primary"
                  :text="$t('global.addField')"
                  :class="{ '__button-group': dropdownItems.length > 0 }"
                  icon="icon-fill_plus"
                  @click="handleAdd"
                />
                <template v-if="dropdownItems.length > 0">
                  <div class="mc-border"></div>
                  <b-dropdown
                    class="mc-buttons-dropdown delivery-header-actions__add-dropdown"
                    variant="primary"
                  >
                    <template #button-content>
                      <i class="mc-icon icon-outline_chevron-down" style="font-size: 18px" />
                    </template>

                    <b-dropdown-item
                      v-for="item in dropdownItems"
                      :key="item.key"
                      @click="
                        item.routeName ? $router.push({ name: item.routeName }) : item.action()
                      "
                    >
                      <div class="d-flex justify-content-between">
                        <div class="me-4">
                          {{ item.label }}
                        </div>
                        <img
                          v-if="item.key == 'accurate'"
                          class="icon-provider"
                          :src="require('@/assets/images/setting/integration/accurate.png')"
                        />
                      </div>
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-button-group>
            </div>
          </template>
        </PageHeader>
      </div>

      <McTable
        :key="tableKey"
        ref="mc-table-delivery"
        :fields="fields"
        :apiClient="apiClient"
        :customHandlers="customHandlers"
        :initialPage="initialParams.page"
        :initialLimit="initialParams.limit"
        :initialSearch="initialParams.search"
        :hasSelectAll="enableSelectAll"
        :hasDelete="false"
        :bulkDeleteApi="true"
        :activeFilters="activeFilters"
        hasEmit:deleteError
        freezeColumnBetween
        stickyHeader
        disableStickyOnMobile
        withCard
        searchable
        @deleteError="handleDeleteError"
        @handleSelectValue="onClickCheckbox($event)"
      >
        <template #before-table="{ isSelected, showDeleteModal }">
          <div class="delivery-table-toolbar mb-3">
            <div class="d-flex justify-content-between delivery-table-toolbar__row">
              <McInlineFilter
                class="delivery-table-toolbar__inline-filter"
                :filterItems="inlineFilterItems"
                :activeKeys="activeFilters['status.name']"
                :hasNavigation="isMobileHeader"
                @update:activeKeys="
                  handleChangeActiveFilters([{ key: 'status.name', values: $event }])
                "
              />

              <div v-if="!isMobileHeader" class="d-flex gap-2 delivery-table-toolbar__actions">
                <McTableFilter
                  :activeFilters="activeFilters"
                  :parseFilterDataMappings="parseFilterDataMappings"
                  @submit="handleChangeActiveFilters"
                  @reset="handleResetFilters"
                >
                  <template
                    #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }"
                  >
                    <InputTableFilter
                      :formValues="formValues"
                      :fullObjects="fullObjects"
                      @update:formValues="updateFormValues($event)"
                      @update:fullObjects="updateFullObjects($event)"
                    />
                  </template>
                </McTableFilter>
                <ActionsDropdown
                  :disabled="!isSelected"
                  :integratedProvider="integratedProvider"
                  :actionHandlers="{
                    handleCreateBill,
                    handleDelete: showDeleteModal,
                    handleDuplicate,
                    handleTransitionToWaitingVendor: changeStatusToWaitingVendor,
                    handleTransitionToConfirmed: changeStatusToConfirmed,
                    handleTransitionToCanceled: changeStatusToCanceled,
                    handleTransitionToRescheduled: changeStatusToRescheduled,
                    handleUpdateUserGroup: updateUserGroup,
                    handleCreateFoDirect: createFoDirect,
                    handleCreateFoTransit: createFoTransit,
                  }"
                  :hasFeatureCreateFoFromDoMGM="hasFeatureCreateFoFromDoMGM"
                />
              </div>
            </div>
          </div>
        </template>
        <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
          <McFilterSelect
            class="delivery-table-toolbar__view-manager"
            :defaultColumnOrder="initialFields"
            :currentColumnOrder="dataFields"
            :saveToLocalStorage="false"
            :storageKey="''"
            :entityType="'DeliveryOrder'"
            :buttonText="isMobileHeader ? '' : undefined"
            :buttonProps="isMobileHeader ? mobileTableToolbarButtonProps : {}"
            storage-scope="company"
            @setColumn="handleSetDeliveryColumns($event, handleSetDataFields)"
          />
        </template>
        <template v-if="isMobileHeader" #afterSearchBar>
          <div class="d-flex gap-2 delivery-table-toolbar__search-actions">
            <McTableFilter
              :activeFilters="activeFilters"
              :parseFilterDataMappings="parseFilterDataMappings"
              :buttonProps="mobileTableToolbarButtonProps"
              @submit="handleChangeActiveFilters"
              @reset="handleResetFilters"
            >
              <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
                <InputTableFilter
                  :formValues="formValues"
                  :fullObjects="fullObjects"
                  @update:formValues="updateFormValues($event)"
                  @update:fullObjects="updateFullObjects($event)"
                />
              </template>
            </McTableFilter>
            <ActionsDropdown
              :disabled="selectedUpdateIds.length === 0"
              :integratedProvider="integratedProvider"
              :actionHandlers="{
                handleCreateBill,
                handleDelete: showTableDeleteModal,
                handleDuplicate,
                handleTransitionToWaitingVendor: changeStatusToWaitingVendor,
                handleTransitionToConfirmed: changeStatusToConfirmed,
                handleTransitionToCanceled: changeStatusToCanceled,
                handleTransitionToRescheduled: changeStatusToRescheduled,
                handleUpdateUserGroup: updateUserGroup,
                handleCreateFoDirect: createFoDirect,
                handleCreateFoTransit: createFoTransit,
              }"
              :hasFeatureCreateFoFromDoMGM="hasFeatureCreateFoFromDoMGM"
              :buttonProps="mobileTableToolbarButtonProps"
            />
          </div>
          <h6
            v-if="selectedUpdateIds.length"
            class="mc-row-check mt-0 delivery-table-toolbar__selected-counter"
          >
            <span class="badge btn-success">{{ selectedUpdateIds.length }}</span>
            {{ $t('global.object.selectedRow') }}
            <a class="color-primary" @click="$refs['mc-table-delivery']?.handleUnselectAll()">{{
              $t('global.action.deselect')
            }}</a>
          </h6>
        </template>
        <template #cell(created_on)="{ value }">
          <template>
            <div>{{ formatIndexDate(value).date }}</div>
            <div class="mc-datetime">
              {{ formatIndexDate(value).time }}
            </div>
          </template>
        </template>

        <template #cell(expected_pickup_on)="{ value }">
          <template>
            <div>{{ formatIndexDate(value).date }}</div>
            <div class="mc-datetime">
              {{ formatIndexDate(value).time }}
            </div>
          </template>
        </template>

        <template #cell(expected_delivered_on)="{ value }">
          <template>
            <div>{{ formatIndexDate(value).date }}</div>
            <div class="mc-datetime">
              {{ formatIndexDate(value).time }}
            </div>
          </template>
        </template>

        <template #cell(line_order)="{ value }">
          {{ value === null || value === undefined || value === '' ? '-' : value }}
        </template>

        <template #[`cell(status.name)`]="{ value, item }">
          <template>
            <span
              v-if="item.status?.color"
              class="__status-dots"
              :style="{ backgroundColor: item.status.color }"
            ></span>
            <span class="mc-text-status">{{
              getDeliveryStatusLabel(item.status || { name: value })
            }}</span>
          </template>
        </template>

        <template #cell(shipment_type)="{ value }">
          {{ value === 'DIRECT' ? 'Direct' : value === 'TRANSIT' ? 'Transit' : '-' }}
        </template>

        <template #[`cell(fleet_task.number)`]="{ value, item }">
          <template v-if="item.shipment_type === 'TRANSIT'">
            <FleetTaskMultiFoDropdown :detail="item" />
          </template>
          <template v-else>
            <router-link
              class="text-secondary-route-link"
              :to="{ name: 'Fleet Task Detail', params: { id: item.fleet_task.id } }"
            >
              {{ item?.fleet_task?.number || '-' }}
            </router-link>
          </template>
        </template>

        <template #[`cell(vendor.name)`]="{ value }">
          {{ value || '-' }}
        </template>

        <template #[`cell(load_weight)`]="{ value }">
          {{ value }}
        </template>

        <template #[`cell(load_volume)`]="{ value }">
          {{ value }}
        </template>

        <!-- Render and format custom fields  -->
        <template v-for="(field, index) in customFields" #[`cell(${field.key})`]="{ value }">
          <template v-if="value.type === 'DATE'">
            <div :key="`div1-${index}`">
              {{ formatIndexDate(value.value).date }}
            </div>
            <div :key="`div2-${index}`" class="mc-datetime">
              {{ formatIndexDate(value.value).time }}
            </div>
          </template>
          <template v-else-if="value.type === 'NUMBER'">
            <div :key="`div3-${index}`" class="td__overflow-ellipsis">
              {{ value.value?.toLocaleString() }}
            </div>
          </template>
          <template v-else>
            <div :key="`div4-${index}`" class="td__overflow-ellipsis">
              {{ value.value }}
            </div>
          </template>
        </template>

        <template #cell(product_categories)="{ value }">
          <McChipList
            v-if="value?.length"
            :items="value"
            :max-visible="3"
            itemTextField="name"
            showAllMode="dropdown"
            :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
            showAllDropdownTrigger="action"
          />
        </template>

        <template #cell(track_do)="data">
          <McButton
            variant="secondary"
            :text="$t('delivery.action.share')"
            icon="icon-fill_share"
            size="sm"
            :disabled="disabledDoTrack(data.item)"
            :displayTooltip="true"
            tooltipText="Belum Dijalankan"
            tooltipPosition="left"
            @click="handleTrackDO(data.item)"
          />
        </template>
      </McTable>
    </div>
    <ModalNotificationV2
      :value.sync="modalWarning"
      :msg="warningMessage"
      :subMsg="subWarningMessage"
      :rejectedDos="rejectedDos"
      :selectedDos="selectedDos"
      :areAllCustomersSame="areAllCustomersSame()"
      @handleContinue="handleContinue"
    />
    <FormulateForm ref="formRef" v-model="formValues" class="mc-form d-none">
      <FormulateInput
        v-bind="accurateCustomer"
        :fullObject.sync="customerFullObject"
        :okTitle="$t('global.next')"
        @update:fullObject="handleProcess($event)"
      />
    </FormulateForm>
    <ModalValueHelp
      ref="mc-modal-waiting-vendor"
      :context="vendorContext"
      :attrs="vendorAttrs"
      @update:fullObject="handleVendorSelect"
    />
    <DefaultTransitionModal
      ref="mc-modal-confirmed"
      :entityData="{ status: { name: 'Draf', label: 'delivery.status.draft' } }"
      :newStatusTransition="{
        status: { name: 'Dikonfirmasi', label: 'delivery.status.confirmed' },
      }"
      :getStatusLabelFn="getDeliveryStatusLabel"
      @transition="handleBulkTransition"
    >
      <template #title>
        <span class="transition-modal-title">{{
          $t('delivery.message.changeDraftStatusToConfirmed')
        }}</span>
      </template>
      <template #confirmationText>
        <p
          v-html="
            $t('delivery.message.confirmChangeStatusSelectedData', {
              count: `<strong>${selectedUpdateIds.length}</strong>`,
            })
          "
        ></p>
      </template>
    </DefaultTransitionModal>
    <DefaultTransitionModal
      ref="mc-modal-canceled"
      :entityData="{ status: { name: uniqueDOStatus[0] ?? '' } }"
      :newStatusTransition="{ status: { name: 'Dibatalkan' } }"
      :hideStatusPreview="uniqueDOStatus.length > 1"
      :getStatusLabelFn="getDeliveryStatusLabel"
      @transition="openCancelModal"
    >
      <template #title>
        <span class="transition-modal-title">{{
          uniqueDOStatus.length > 1
            ? $t('delivery.message.changeStatusToCanceled')
            : $t('delivery.message.changeStatusToCanceledFromStatus', {
                status: translateDeliveryStatus(uniqueDOStatus[0]),
              })
        }}</span>
      </template>
      <template #confirmationText>
        <p
          v-html="
            $t('delivery.message.confirmChangeStatusSelectedData', {
              count: `<strong>${selectedUpdateIds.length}</strong>`,
            })
          "
        ></p>
      </template>
    </DefaultTransitionModal>
    <TransitionToCancelModal
      ref="mc-modal-cancel-reason"
      :entityData="{}"
      :newStatusTransition="{ status: { name: 'CANCELED' } }"
      :isFromList="true"
      @transition="confirmCancel"
    >
      <template #header>
        <span
          class="d-block mb-2"
          v-html="
            $t('delivery.message.confirmCancelSelectedData', {
              count: `<strong>${selectedUpdateIds.length}</strong>`,
            })
          "
        ></span>
      </template>
    </TransitionToCancelModal>
    <TransitionToRescheduleModal
      ref="mc-modal-rescheduled"
      :entityData="{}"
      :newStatusTransition="{ status: { name: 'RESCHEDULED' } }"
      :isPickedToRescheduled="isPickedToRescheduled"
      :isFromList="true"
      :selectedIds="selectedUpdateIds"
      @transition="confirmReschedule"
    />
    <UpdateUserGroupModal
      ref="mc-modal-update-user-group"
      @change-user-group="handleChangeUserGroup"
    />
    <ModalShareTracking :detailData="dataDO" />
    <ModalValueHelp
      ref="mc-modal-user-group"
      :context="userGroupContext"
      :attrs="userGroupAttrs"
      @update:fullObject="handleUserGroupSelect"
    />
    <ScheduleFleetTaskBulk
      :selectedUpdateIds="selectedUpdateIds"
      @handleCancel="handleCancelScheduleFleetTaskBulk"
    />
    <ModalConfirmSummaryTemperature
      :data="dataGapTemprerature"
      :modal-id="'modal-confirm-summary-temperature-attach'"
      @handleSubmit="handleApplyCreateFO"
    />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapGetters } from 'vuex'

import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McInlineFilter from '@/components/McInlineFilter'
import McTableFilter from '@/components/McTableFilter'
import McFilterSelect from '@/components/McFilterSelect'
import McListDownloader from '@/components/McListDownloader'
import InputTableFilter from './components/InputTableFilter'
import ActionsDropdown from './components/ActionsDropdown/index'
import DefaultTransitionModal from '@/components/McTransitionStatus/DefaultTransitionModal.vue'
import ModalValueHelp from '@/components/formulate/ValueHelpInputV2/components/ModalValueHelp'
import McChipList from '@/components/McChipList'

import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import * as Transition from '@/services/rest-api/lib/delivery/transition'
import * as Integration from '@/services/rest-api/lib/delivery/integration'
import * as CustomField from '@/services/rest-api/lib/setting/custom-field'
import * as Invoice from '@/services/rest-api/lib/invoice/invoice'
import * as Vendor from '@/services/rest-api/lib/vendor/vendor'
import * as WaitingVendorTransition from '@/services/rest-api/lib/backend-for-frontend/delivery-order/transition'
import * as UserGroup from '@/services/rest-api/lib/delivery/userGroup'

import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'

import accurateCustomerFields from './lib/fields/accurateCustomerFields'
import listTableFields from './lib/fields/listTableFields'
import inlineFilterItems from './lib/constants/inline-filter-items'
import filterMappings from './lib/constants/filter-mappings'
import apiErrorMappings from './lib/constants/api-error-mappings'
import parseFilterDataMappings from './lib/constants/parse-filter-data-mappings'
import errorMessageList from '../invoice/lib/constants/error-message-mapper.js'
import formatFilterMappings from './lib/constants/format-filter-mappings'
import { statusIdToLabelMap, statusIdToNameMap, statusNameToIdMap } from './lib/constants/status'
import { wizardStatusItems } from './lib/constants/wizard-status-items'

import ModalNotificationV2 from './components/Provider/ModalNotificationV2'
import ModalShareTracking from './components/Modal/ShareTracking'
import ModalConfirmSummaryTemperature from './components/Modal/ConfirmSummaryTemperature.vue'

import { mapMutations } from 'vuex'
import { formatIndexDate } from '@/helpers/tzDate'
import { hasLineOrderFeatureAccess } from '@/helpers/tms-line-order'
import { Helper } from '@/mixin/helper'
import { TableHelper } from '@/mixin/table'
import { formatNumber } from '@/helpers/format-number.js'

import moment from 'moment'
import omitBy from 'lodash/omitBy'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import TransitionToCancelModal from './components/TransitionToCancelModal.vue'
import TransitionToRescheduleModal from './components/TransitionToRescheduleModal.vue'
import UpdateUserGroupModal from './components/UpdateUserGroupModal.vue'
import ScheduleFleetTaskBulk from './components/ScheduleFleetTaskBulk.vue'
import FleetTaskMultiFoDropdown from './components/FleetTaskMultiFoDropdown.vue'

moment.locale('id')

const CREATE_FO_ALLOWED_STATUSES = [
  'Dikonfirmasi',
  'Dijadwalkan Ulang',
  'Dijadwalkan',
  'Dijalankan',
  'Diambil',
  'Sedang Transit',
  'Menunggu Vendor',
  'Ditolak',
]

/**
 * Starter component
 */
export default {
  page: {
    title: 'Delivery',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McTable,
    McInlineFilter,
    McTableFilter,
    McFilterSelect,
    DefaultTransitionModal,
    InputTableFilter,
    ActionsDropdown,
    McListDownloader,
    ModalNotificationV2,
    TransitionToCancelModal,
    ModalValueHelp,
    ModalShareTracking,
    TransitionToRescheduleModal,
    UpdateUserGroupModal,
    McChipList,
    ScheduleFleetTaskBulk,
    FleetTaskMultiFoDropdown,
    ModalConfirmSummaryTemperature,
  },
  data() {
    return {
      metadata: null,
      deliveryIntegrationProvider: null,

      showIntegrationProviderDropdown: false,
      modalWarning: false,
      tableKey: 1,

      warningMessage: '',
      subWarningMessage: '',

      customFields: [],
      selectedDos: [],
      rejectedDos: [],

      activeFilters: {},
      params: {},
      formValues: {},
      customerFullObject: {},
      initialParams: {
        page: 1,
        limit: 10,
        search: '',
      },
      customHandlers: {
        handleClickRow: this.handleClickRow,
      },
      mappingErrorStatus: {
        DRAFT: 'Draf',
        CANCELED: 'Dibatalkan',
      },
      selectedUpdateIds: [],

      vendorContext: {
        label: 'Vendor',
        model: {},
        blurHandler: () => {},
      },
      dataDO: {},
      userGroupContext: {
        label: 'User Group',
        model: {},
        blurHandler: () => {},
      },
      skipValidationUserGroup: false,
      isPickedToRescheduled: false,
      saveToLocalStorage: true,
      isTransitionLoading: false,
      dataGapTemprerature: null,
      skipCheckTemp: false,
      dontShowAgain: false,
      pendingCreateFoModal: null,
      isMobileHeader: false,
      hasPageScrolled: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagData: 'identity/getFeatureFlagData',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    title() {
      return this.$t('delivery.title.delivery')
    },
    subTitle() {
      return this.$t('delivery.subtitle')
    },
    buttonScreenBreakpoint() {
      return 768
    },
    downloadButtonProps() {
      return {
        screenBreakpoint: this.buttonScreenBreakpoint,
        tooltipPosition: 'bottom',
      }
    },
    mobileTableToolbarButtonProps() {
      return {
        screenBreakpoint: 992,
        tooltipPosition: 'bottom',
      }
    },
    isStickyHeader() {
      return this.isMobileHeader && this.hasPageScrolled
    },
    dropdownItems() {
      const items = [
        {
          key: 'create-bulk',
          routeName: 'Delivery Create Bulk',
          label: this.$t('delivery.action.createBulk'),
        },
      ]

      if (this.showIntegrationProviderDropdown) {
        items.push({
          key: 'accurate',
          routeName: 'Delivery Accurate',
          label: this.$t('delivery.action.fromAccurate'),
        })
      }

      return items
    },
    fields() {
      const fields = []

      listTableFields(
        this.customFields.map((field) => ({ label: field.name, key: field.key })),
        {
          showLineOrder: this.hasCustomerFeatureLineOrder,
          t: (key) => this.$t(key),
        }
      ).forEach((field) => {
        if (field.isShow === false) return
        if (field.scopes) {
          const hasAccess = this.hasAccessAll(field.scopes)
          if (!hasAccess) return
        }

        fields.push(field)
      })

      TableHelper.methods.addStickyColumnByKeys(fields, ['number', 'status.name'])

      return fields
    },
    hasCustomerFeatureLineOrder() {
      return hasLineOrderFeatureAccess()
    },
    vendorAttrs() {
      return {
        name: 'vendor',
        modalTitle: this.$t('delivery.action.selectVendor'),
        trackBy: 'id',
        tableFields: [
          {
            key: 'name',
            name: 'name',
            label: this.$t('delivery.fields.vendorName'),
            isEditable: true,
          },
        ],
        listGetter: async function (filters) {
          const res = await Vendor.get(filters, ['paired_company_id gt 0'])

          return res
        },
        isMandatory: true,
      }
    },

    userGroupAttrs() {
      return {
        name: 'user_group',
        modalTitle: this.$t('delivery.action.selectUserGroup'),
        trackBy: 'id',
        tableFields: [
          {
            label: this.$t('delivery.fields.userGroup'),
            key: 'name',
            isSortable: false,
          },
          {
            label: this.$t('delivery.fields.vehicleGroup'),
            key: 'vgroups',
            isSortable: false,
            formatter: (value) => {
              return value?.length != null
                ? value.length === 0
                  ? this.$t('delivery.fields.allVehicleGroup')
                  : value.length
                : '-'
            },
          },
        ],
        listGetter: async function (opts) {
          let result = {}
          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_USER_GROUP,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          const ugroups = res.data.ugroups.ugroups

          result = {
            metadata: {
              total_count: ugroups.pagination.total,
              page: ugroups.pagination.page,
            },
            data: {
              paginated_result: ugroups.data,
            },
          }

          return result
        },
        isMandatory: true,
      }
    },

    apiClient() {
      let getDeliveryOrder = Delivery.get(
        null,
        this.handleSetParams,
        this.handleSetMetadata,
        this.handleSetCustomFields
      )
      if (Object.keys(this.activeFilters).length > 0) {
        let queryParamFilters = []

        Object.keys(this.activeFilters).forEach((key) => {
          let filterValue = this.activeFilters[key]
          if (key === 'shipment_type') {
            if (filterValue.all === true) {
              return
            }

            filterValue = Object.entries(filterValue)
              .filter(([_, value]) => value === true)
              .map(([key]) => key)
          }
          const filter = filterMappings[key]?.(key, filterValue)
          if (!filter) return
          queryParamFilters.push(filter)

          getDeliveryOrder = Delivery.get(
            queryParamFilters,
            this.handleSetParams,
            this.handleSetMetadata,
            this.handleSetCustomFields
          )
        })
      }

      return {
        get: getDeliveryOrder,
        delete: Delivery.deleteMany,
      }
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    downloadPageOptions() {
      return [
        { label: this.$t('global.currentPage'), amount: this.metadata?.count, key: 'current_page' },
        { label: this.$t('global.allPages'), amount: this.metadata?.total_count, key: 'all' },
      ]
    },
    inlineFilterItems() {
      return inlineFilterItems(this)
    },
    hasTmsAccurateScopes() {
      return this.hasAccessAll([
        'tms-integration-accurate:delete',
        'tms-integration-accurate:update',
        'tms-integration-accurate:read',
        'tms-integration-accurate:create',
      ])
    },
    accurateCustomer() {
      return accurateCustomerFields
    },
    integratedProvider() {
      return this.deliveryIntegrationProvider
    },
    viewManagerStorageKey() {
      return 'ViewManagerDelivery'
    },
    enableSelectAll() {
      // Disable select all for 'Semua' filter
      return this.activeFilters['status.name']?.length > 0
    },
    uniqueDOStatus() {
      // If status active, return the status
      if (this.activeFilters['status.name']?.length > 0) {
        return [statusIdToNameMap[this.activeFilters['status.name'][0]]]
      }

      const uniqueStatus = []
      for (const deliveryOrder of this.selectedDos) {
        const status = deliveryOrder.status.name
        if (!uniqueStatus.includes(status)) {
          uniqueStatus.push(status)
        }
      }

      return uniqueStatus
    },
    hasTmsUserGroupAccess() {
      return (
        this.hasAccessAll([
          'tms-user-group:create',
          'tms-user-group:delete',
          'tms-user-group:read',
          'tms-user-group:update',
        ]) && Helper.methods.isSuperUser()
      )
    },
    hasFeatureCreateFoFromDoMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(Helper.methods.getCompanyId()) &&
        data?.features?.create_fo_from_do
      )
    },
    increaseDeliveryOrderLimit() {
      return this.getFeatureFlagValue('increaseDeliveryOrderLimitFlag')
    },
  },
  watch: {
    deliveryIntegrationProvider() {
      this.showIntegrationProviderDropdown =
        this.hasTmsAccurateScopes && this.deliveryIntegrationProvider === 'ACCURATE'
    },
  },
  created() {
    this.setFilterFromURL()
  },
  mounted() {
    this.checkHeaderViewport()
    this.handleWindowScroll()
    window.addEventListener('resize', this.checkHeaderViewport)
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
    if (this.hasTmsAccurateScopes) {
      this.setDeliveryIntegrationProvider()
      this.getFeatureFlagData('reSyncDOAccurate').value && Delivery.syncBulk()
    }
    this.$store.dispatch('delivery/setShipmentTypeFilters', { all: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkHeaderViewport)
    window.removeEventListener('scroll', this.handleWindowScroll)
  },
  methods: {
    handleWindowScroll() {
      this.hasPageScrolled = window.scrollY > 8
    },
    checkHeaderViewport() {
      this.isMobileHeader = window.innerWidth < this.buttonScreenBreakpoint
    },
    showTableDeleteModal() {
      this.$refs['mc-table-delivery']?.showDeleteModal()
    },
    getDeliveryStatusLabel(status) {
      if (!status) return '-'
      if (status.label) return this.$t(status.label)

      const statusId = status.id || statusNameToIdMap[status.name]
      const statusLabel = statusIdToLabelMap[statusId]

      return statusLabel ? this.$t(statusLabel) : status.name
    },
    formatNumber(value) {
      return formatNumber(value, 3) || '-'
    },
    translateDeliveryStatus(statusName) {
      const matchedStatus = Object.values(wizardStatusItems(this)).find(
        (status) => status.key === statusName
      )

      return matchedStatus?.label || statusName
    },
    formatIndexDate,
    ...mapMutations('invoice', ['setSelectedDos', 'setCustomer']),
    handleClickRow(item) {
      const query = this.$route.query
      this.$router.push({ name: 'Delivery Detail', params: { id: item.id }, query })
    },
    handleAdd() {
      const query = this.$route.query
      this.$router.push({ name: 'Delivery Create', query })
    },
    handleDeleteError(errors) {
      const errorMessage = apiErrorMappings.bulkDelete[errors.message] || errors.message
      this.$mcToast.error(errorMessage)
    },
    resetSelectedIds() {
      this.vendorContext.model = {}
      this.userGroupContext.model = {}
      this.skipValidationUserGroup = false
      this.selectedDos = []
      this.selectedUpdateIds = []
      this.$refs['mc-table-delivery'].ResetSelects()
      this.$refs['mc-table-delivery']?.handleUnselectAll()
    },
    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null, undefined, or empty string
        if (filter.values === null || filter.values === undefined || filter.values === '') return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })

      this.resetSelectedIds()
    },
    handleDownload(format, dataAmount) {
      // Remove undefined params
      const params = omitBy(
        {
          ...this.params,
          format,
        },
        (value) => value === undefined
      )

      if (dataAmount === 'all') {
        delete params['limit']
        delete params['page']
      }

      window.open(
        `${process.env.VUE_APP_API_ORDER_URL}/delivery-order?${new URLSearchParams(
          params
        ).toString()}`,
        '_self'
      )
    },
    handleSetParams(params) {
      this.params = params
      this.setURLParams(params)
    },
    handleSetMetadata(metadata) {
      this.metadata = metadata
    },
    handleSetDeliveryColumns(fields, handleSetDataFields) {
      let normalizedFields = [...fields]

      // Fix line_order position
      const numberIndex = normalizedFields.findIndex((field) => field.key === 'number')
      const lineOrderIndex = normalizedFields.findIndex((field) => field.key === 'line_order')

      if (numberIndex !== -1 && lineOrderIndex !== -1) {
        const [lineOrderField] = normalizedFields.splice(lineOrderIndex, 1)
        const insertIndex = lineOrderIndex > numberIndex ? numberIndex + 1 : numberIndex
        normalizedFields.splice(insertIndex, 0, lineOrderField)
      }

      // Fix custom fields position (insert after destination_address.city, before load_count)
      const customFieldKeys = this.customFields.map((cf) => cf.key)
      const customFields = normalizedFields.filter((field) => customFieldKeys.includes(field.key))

      // Remove custom fields from their current positions
      normalizedFields = normalizedFields.filter((field) => !customFieldKeys.includes(field.key))

      // Find insertion point: after destination_address.city
      const insertionIndex =
        normalizedFields.findIndex((field) => field.key === 'destination_address.city') + 1

      // Insert custom fields in correct position
      if (insertionIndex > 0) {
        normalizedFields.splice(insertionIndex, 0, ...customFields)
      }

      handleSetDataFields(normalizedFields)
    },
    // change current URL based on params changes
    setURLParams(params) {
      const newParams = {}

      // loop the params to set the newParams
      for (const [key, val] of Object.entries(params)) {
        // skip the undefined value
        if (val == undefined) continue

        // immediately set the value if it's not an Array
        if (!Array.isArray(val)) {
          newParams[key] = typeof val === 'string' ? val : val.toString()

          continue
        }

        // value with Array type usually is filters/sorts, so we need
        // to iterate it and set to each key
        val.forEach((item, index) => {
          newParams[key + index] = item
        })
      }

      // check if the URL has same values with new params
      const isSameParams = isEqual(newParams, this.$route.query)
      if (isSameParams) return

      // redirect to new query params
      const urlPath = '/delivery?' + new URLSearchParams(newParams).toString()
      this.$router.replace(urlPath)
    },
    // change filters from current URL
    setFilterFromURL() {
      const activeFilters = new Map()
      for (const [key, val] of Object.entries(this.$route.query)) {
        // set initial params exclude filters (page, limit, search)
        if (!key.includes('filters')) {
          let value
          if (key === 'search') {
            value = val || '-'
          } else if (key === 'limit') {
            value = [10, 25, 50, 100].includes(val) ? Number(val) || 1 : 10
          } else if (key === 'page') {
            value = Number(val) || 1
          }

          this.$set(this.initialParams, key, value)
          continue
        }

        const field = val.split(' ')[0]

        // TODO: Set filters from table filter
        if (field !== 'status.name') continue

        const { op, value } = formatFilterMappings[field](val)
        let newKey = `${field}:${op}`

        if (field === 'status.name') {
          newKey = field
        }

        // set new key in active filters
        activeFilters.set(newKey, value)
      }

      // if there are active filters, set the new values
      if (activeFilters.size == 0) return

      this.activeFilters = Object.fromEntries(activeFilters)
    },
    async handleSetCustomFields(fields) {
      if (fields.length === 0) {
        const res = await CustomField.get()

        if (res.errors) return

        this.customFields = res.data?.filter((field) => field.is_active)

        return
      }

      this.customFields = fields
    },
    async handleDuplicate() {
      const selectedIds = this.$refs['mc-table-delivery'].selectedIds
      const ids = []

      Object.keys(selectedIds).forEach((id) => {
        if (selectedIds[id] == false) return

        ids.push(id)
      })

      if (ids.length == 0) {
        this.$mcToast.error(this.$t('delivery.alert.duplicateNoSelection'))
        return
      }

      const selectedUserGroupId = this.getSelectedUserGroupId(this.userGroupContext.model)
      if (this.hasTmsUserGroupAccess && (!this.skipValidationUserGroup || !selectedUserGroupId)) {
        this.skipValidationUserGroup = false
        this.$refs['mc-modal-user-group'].$refs['value-help-modal-ref'].show()
        return
      }

      let data = { ids }
      if (this.hasTmsUserGroupAccess) {
        data = {
          ...data,
          user_group_id: Number(selectedUserGroupId),
        }
      }

      const res = await Delivery.duplicate(data)
      if (res.errors) {
        this.$mcToast.error(this.$t('delivery.alert.duplicateFailed'))
        return
      }

      this.resetSelectedIds()

      this.tableKey += 1
      const { success: totalSuccess, failed: totalFailed } = res.status

      if (totalFailed) {
        this.$mcToast.error(this.$t('delivery.alert.duplicateFailedCount', { count: totalFailed }))
      }
      if (totalSuccess) {
        this.$mcToast.success(
          this.$t('delivery.alert.duplicateSuccessCount', { count: totalSuccess })
        )
      }
    },
    // expected to return other provider name later.
    async setDeliveryIntegrationProvider() {
      const redirectUrl = window.location.origin + window.location.pathname
      const list = await Integration.getUserIntegrationList(redirectUrl)

      for (let i = 0; i < list.providers?.length; i++) {
        const provider = list.providers[i]
        if (provider.code === 'ACCURATE' && provider.is_integrated) {
          this.deliveryIntegrationProvider = provider.code
          return
        }
      }

      this.deliveryIntegrationProvider = null
      return
    },
    async handleCreateBill() {
      this.rejectedDos = []
      this.warningMessage = ''

      // Check are all customer same?
      if (!this.areAllCustomersSame()) {
        this.warningMessage = this.$t('delivery.invoice.warningMessage.billingSameCustomer')
        this.modalWarning = true
        return
      }

      try {
        const payload = {
          delivery_orders: this.selectedDos,
        }
        const { errors, message } = await Invoice.store(payload)

        if (errors && typeof errors === 'object') {
          const errorKeys = ['invoice_id', 'status', 'external_reference']
          let errorMessage = Helper.methods.withErrorHandler(
            { errors },
            errorMessageList,
            errorKeys,
            true
          )

          // NOTE: validation same customer, only on FE side
          if (typeof errorMessage === 'string') {
            this.$mcToast.error(errorMessage)
          }

          if (Array.isArray(errorMessage)) {
            this.warningMessage = this.$t('delivery.invoice.warningMessage.doCantBilled')
            errorMessage.forEach((errorMessageItem) => {
              for (const [key, val] of Object.entries(errorMessageItem)) {
                if (['invoice_id', 'status'].includes(key)) {
                  this.modalWarning = true
                  this.rejectedDos = [...this.rejectedDos, ...errorMessageItem.id.split(',')]
                }

                // Update status DO, if DO status changed suddenly
                if (key === 'status') {
                  const indexOfDo = this.selectedDos.findIndex((v) => v.id === errorMessageItem.id)
                  if (indexOfDo !== -1) {
                    this.selectedDos[indexOfDo].status.name = this.mappingErrorStatus[val]
                  }
                }

                if (key === 'external_reference') {
                  this.$mcToast.error(errorMessageItem.errorMessage)
                  break
                }
              }
            })

            if (this.rejectedDos.length === this.selectedDos.length) {
              this.subWarningMessage = this.$t('global.alert.text.pleaseCheckAgain')
            }

            if (
              this.rejectedDos.length > 0 &&
              this.rejectedDos.length !== this.selectedDos.length
            ) {
              this.subWarningMessage = this.$t('delivery.invoice.warningMessage.continueWithoutDO')
            }
          }
          return
        }

        if (errors === true) {
          const errMsg = message
            ? this.$t(errorMessageList[message])
            : Helper.methods.withErrorHandler()
          this.$mcToast.error(errMsg)
          return
        }
      } catch (error) {
        this.$mcToast.error(Helper.methods.withErrorHandler())
        console.error(error)
        return
      }

      // Open customer
      this.$bvModal.show(`customer_id-modal-value-help`)
    },
    onClickCheckbox(ids) {
      if (Object.keys(ids).length === 0) {
        this.selectedDos = []
        this.selectedUpdateIds = []
        return
      }

      const items = this.$refs['mc-table-delivery'].items
      for (const [key, val] of Object.entries(ids)) {
        const delivery = items.find((v) => v.id === key)
        const index = this.selectedDos.findIndex((v) => v.id === key)

        if (!val) {
          if (index !== -1) {
            this.selectedDos.splice(index, 1)
          }
          continue
        }
        if (val && delivery && index === -1) {
          this.selectedDos.push(delivery)
        }
      }

      for (const [key, value] of Object.entries(ids)) {
        if (value) {
          if (!this.selectedUpdateIds.some((item) => item.id === key)) {
            this.selectedUpdateIds.push({ id: key })
          }
        } else {
          const index = this.selectedUpdateIds.findIndex((item) => item.id === key)
          if (index !== -1) {
            this.selectedUpdateIds.splice(index, 1)
          }
        }
      }
    },
    areAllCustomersSame() {
      const deliveries = this.selectedDos
      // Extract the unique customer names from the array
      const uniqueCustomers = [...new Set(deliveries.map((item) => item.billed_customer.name))]

      // If there's only one unique customer, return true; otherwise, return false
      return uniqueCustomers.length === 1
    },
    handleProcess(customer) {
      if (isEmpty(customer)) {
        this.$mcToast.error(this.$t('delivery.invoice.warningMessage.selectCustomerToContiue'))
        return
      }
      this.setSelectedDos(this.selectedDos)
      this.setCustomer(customer)
      this.$router.push({ name: 'Delivery Invoice' })
    },
    handleContinue() {
      this.modalWarning = false
      this.warningMessage = ''
      this.selectedDos = [...this.selectedDos.filter((v) => !this.rejectedDos.includes(v.id))]
      const tableDelivery = this.$refs['mc-table-delivery']
      this.rejectedDos.forEach((id) => {
        if (tableDelivery.selectedIds[id]) {
          tableDelivery.selectedIds[id] = false
          tableDelivery.selectedIdsLength -= 1
        }
      })
      this.rejectedDos = []
      this.$bvModal.show(`customer_id-modal-value-help`)
    },
    validateSelectionWaitingForVendor() {
      if (this.selectedUpdateIds.length === 0) {
        this.$mcToast.error(this.$t('delivery.alert.pleaseSelectAtLeastOneDelivery'))
        return false
      }

      const uniqueStatuses = new Set(this.uniqueDOStatus)

      const hasStatus = {
        invalid: [...uniqueStatuses].some((status) => status !== 'Dikonfirmasi'),
      }

      if (hasStatus.invalid) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.hasDeliveryWithNonConfirmedStatus', {
                  confirmed: `<strong>${this.$t('delivery.status.confirmed')}</strong>`,
                }),
              },
            ],
          },
        })
        return false
      }

      return true
    },
    validateSelection(canceled = false) {
      if (this.selectedUpdateIds.length === 0) {
        this.$mcToast.error(this.$t('delivery.alert.pleaseSelectAtLeastOneDelivery'))
        return false
      }

      const uniqueStatuses = new Set(this.uniqueDOStatus)

      const hasStatus = {
        picked: uniqueStatuses.has('Diambil'),
        confirmed: uniqueStatuses.has('Dikonfirmasi'),
        scheduled: uniqueStatuses.has('Dijadwalkan'),
        started: uniqueStatuses.has('Dijalankan'),
        delivered: uniqueStatuses.has('Terkirim'),
        canceled: uniqueStatuses.has('Dibatalkan'),
        rescheduled: uniqueStatuses.has('Dijadwalkan Ulang'),
        invalid: [...uniqueStatuses].some(
          (status) => status !== 'Draf' && status !== 'Dikonfirmasi'
        ),
      }

      if (
        !canceled &&
        (hasStatus.confirmed ||
          hasStatus.scheduled ||
          hasStatus.started ||
          hasStatus.picked ||
          hasStatus.delivered ||
          hasStatus.rescheduled ||
          hasStatus.canceled)
      ) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.hasDeliveryWithNonDraftStatus', {
                  draft: `<strong>${this.$t('delivery.status.draft')}</strong>`,
                }),
              },
            ],
          },
        })
        return false
      }

      if (hasStatus.invalid) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.hasDeliveryWithNonDraftOrConfirmedStatus', {
                  draft: `<strong>${this.$t('delivery.status.draft')}</strong>`,
                  confirmed: `<strong>${this.$t('delivery.status.confirmed')}</strong>`,
                }),
              },
            ],
          },
        })
        return false
      }

      return true
    },
    changeStatusToWaitingVendor() {
      if (this.validateSelectionWaitingForVendor()) {
        this.$refs['mc-modal-waiting-vendor'].$refs['value-help-modal-ref'].show()
      }
    },

    async handleVendorSelect(vendor) {
      try {
        const ids = []

        this.selectedUpdateIds.forEach((value) => {
          ids.push(value.id)
        })

        const res = await WaitingVendorTransition.transition({
          ids,
          status: 'WAITING_FOR_VENDOR',
          fields: {
            vendor_id: vendor.id,
          },
        })

        if (res.data.failed_count > 0) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: `<strong>${res.data.failed_count} DO</strong> ${this.$t(
                    'delivery.alert.failedToSendRequest'
                  )}`,
                },
              ],
            },
          })
        }

        if (res.data.success_count > 0) {
          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: `<strong>${res.data.success_count} DO</strong> ${this.$t(
                    'delivery.alert.successSendRequest'
                  )}`,
                },
              ],
            },
          })
        }
      } catch (e) {
        this.$mcToast.error(this.$t('global.somethingWentWrongTryAgainLater'))
      } finally {
        this.tableKey += 1
        this.resetSelectedIds()
      }
    },

    changeStatusToConfirmed() {
      if (this.validateSelection()) {
        this.$refs['mc-modal-confirmed'].show()
      }
    },

    changeStatusToCanceled() {
      this.$refs['mc-modal-cancel-reason'].show()
    },
    changeStatusToRescheduled() {
      const skipConfirmationReschedule = (this.activeFilters['status.name'] || []).some((s) =>
        [
          'DRAFT',
          'CONFIRMED',
          'SCHEDULED',
          'DELIVERED',
          'CANCELED',
          'IN_TRANSIT',
          'RESCHEDULED',
        ].includes(s)
      )

      if (skipConfirmationReschedule && this.selectedUpdateIds.length > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.rescheduleInvalidStatusCount', {
                  count: this.selectedUpdateIds.length,
                }),
              },
            ],
          },
        })
        this.resetSelectedIds()
        return
      }

      if (
        this.uniqueDOStatus.includes('Diambil') ||
        (this.activeFilters['status.name'] || []).includes('PICKED')
      ) {
        this.isPickedToRescheduled = true
      }
      this.$refs['mc-modal-rescheduled'].show()
    },
    openCancelModal() {
      this.$refs['mc-modal-cancel-reason'].show()
    },
    confirmCancel(transitionData) {
      this.handleBulkTransitionV2(transitionData)
    },
    confirmReschedule(transitionData) {
      this.handleBulkTransitionV2(transitionData)
    },
    async handleBulkTransition(data) {
      try {
        this.isTransitionLoading = true
        // TODO: merge into one modal
        this.$refs['mc-modal-waiting-vendor'].$refs['value-help-modal-ref'].hide()
        this.$refs['mc-modal-confirmed'].hide()
        this.$refs['mc-modal-canceled'].hide()
        this.$refs['mc-modal-cancel-reason'].hide()
        const statusName = data.transition.status.name

        // const promises = []
        // for (const delivery of this.selectedUpdateIds) {
        //   promises.push(
        //     Transition.transition(delivery.id, {
        //       status: statusNameToIdMap[statusName],
        //       fields: data.additionalPayload?.fields,
        //     })
        //   )
        // }
        //
        // const results = await Promise.all(promises)
        // const errorCount = results.filter((result) => result !== 'OK').length
        // const successCount = results.length - errorCount

        let errorCount = 0
        let successCount = 0

        for (const delivery of this.selectedUpdateIds) {
          try {
            const result = await Transition.transition(delivery.id, {
              status: statusNameToIdMap[statusName],
              fields: data.additionalPayload?.fields,
            })

            if (result === 'OK') {
              successCount++
            } else {
              errorCount++
            }
          } catch (_) {
            errorCount++
          }
        }

        if (successCount > 0) {
          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('delivery.alert.bulkTransitionSuccess', {
                    count: successCount,
                    status: `<strong>${this.translateDeliveryStatus(statusName)}</strong>`,
                  }),
                },
              ],
            },
          })
        }
        if (errorCount > 0) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('delivery.alert.bulkTransitionFailed', {
                    count: errorCount,
                  }),
                },
              ],
            },
          })
        }
        this.tableKey += 1
      } finally {
        this.resetSelectedIds()
        this.isTransitionLoading = false
      }
    },
    async handleBulkTransitionV2(transitionData) {
      this.isTransitionLoading = true
      this.$refs['mc-modal-cancel-reason'].hide()
      this.$refs['mc-modal-rescheduled'].hide()
      const transitionTo = transitionData.transition.status.name

      const data = {
        ids: this.selectedUpdateIds.map((item) => item.id),
        status: transitionTo,
        fields: transitionData.additionalPayload.fields,
      }

      const res = await Transition.bulkTransition(data)

      const statusName = Object.keys(statusNameToIdMap).find(
        (k) => statusNameToIdMap[k] === transitionTo
      )

      const hasInvalidFleetTaskStatus = (errors) => {
        return errors?.['fields.fleet_task_uuid']?.includes('invalid fleet_task status') || false
      }

      if (hasInvalidFleetTaskStatus(res?.errors)) {
        this.$mcToast.error(this.$t('delivery.alert.cannotCancelWaitingFleetTask'))
        this.isTransitionLoading = false
        this.tableKey += 1
        this.isPickedToRescheduled = false
        this.resetSelectedIds()
        return
      }

      let successCount = res.data.succeeded_ids.length
      let errorCount = res.data.failed_ids.length

      if (successCount > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.bulkTransitionSuccessCount', {
                  count: successCount,
                  status: this.translateDeliveryStatus(statusName).toLowerCase(),
                }),
              },
            ],
          },
        })
      }

      if (errorCount > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.bulkTransitionFailedCount', {
                  count: errorCount,
                  status: this.translateDeliveryStatus(statusName).toLowerCase(),
                }),
              },
            ],
          },
        })
      }

      this.isTransitionLoading = false
      this.tableKey += 1
      this.isPickedToRescheduled = false
      this.resetSelectedIds()
    },
    handleResetFilters() {
      this.$store.dispatch('delivery/setShipmentTypeFilters', { all: true })
    },
    disabledDoTrack(data) {
      if (data.track_link) {
        return false
      }
      return true
    },
    handleTrackDO(data) {
      this.dataDO = data
      this.$bvModal.show('modal-link')
    },
    getSelectedUserGroupId(data) {
      if (!data) return ''
      if (typeof data === 'object' && data !== null) {
        return data.user_group_id || data.id || ''
      }
      return data
    },
    handleUserGroupSelect(data) {
      const userGroupId = this.getSelectedUserGroupId(data)

      if (!userGroupId) {
        this.$mcToast.error(this.$t('delivery.alert.userGroupRequired'))
        this.skipValidationUserGroup = false
        this.$refs['mc-modal-user-group'].$refs['value-help-modal-ref'].hide()
        return
      }

      this.userGroupContext.model = userGroupId
      this.skipValidationUserGroup = true

      this.$nextTick(() => {
        this.handleDuplicate()
      })
    },
    updateUserGroup() {
      const skipUpdateUserGroup = (this.activeFilters['status.name'] || []).some(
        (s) => !['DRAFT', 'CONFIRMED', 'RESCHEDULED'].includes(s)
      )

      if (skipUpdateUserGroup && this.selectedUpdateIds.length > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.updateUserGroupInvalidStatus', {
                  count: this.selectedUpdateIds.length,
                }),
              },
            ],
          },
        })
        this.resetSelectedIds()
        return
      }
      this.$refs['mc-modal-update-user-group'].show()
    },
    async handleChangeUserGroup(userGroupData) {
      const data = {
        delivery_order_uuids: this.selectedUpdateIds.map((item) => item.id),
        user_group_id: userGroupData.user_group_id,
      }

      const res = await UserGroup.update(data)

      const totalIds = this.selectedUpdateIds.length
      if (res === 'OK') {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.updateUserGroupSuccess', {
                  count: totalIds,
                }),
              },
            ],
          },
        })
      } else {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.updateUserGroupFailed', {
                  count: totalIds,
                }),
              },
            ],
          },
        })
      }

      this.$refs['mc-modal-update-user-group'].hide()
      this.tableKey += 1
      this.resetSelectedIds()
    },
    getSelectedDeliveryIds() {
      const selectedIds = this.$refs['mc-table-delivery'].selectedIds

      return Object.keys(selectedIds).filter((id) => selectedIds[id] !== false)
    },
    validateCreateFoSelection() {
      const ids = this.getSelectedDeliveryIds()

      if (ids.length === 0) {
        this.$mcToast.error(this.$t('delivery.alert.selectDeliveryFirst'))
        return false
      }

      const deliveryOrderLimit = this.increaseDeliveryOrderLimit ? 100 : 50
      if (ids.length > deliveryOrderLimit) {
        this.$mcToast.error(this.$t(deliveryOrderLimit? 'delivery.alert.scheduleFailedMax100' : 'delivery.alert.scheduleFailedMax50'))
        return false
      }

      const hasInvalidStatus = [...new Set(this.uniqueDOStatus)].some(
        (status) => !CREATE_FO_ALLOWED_STATUSES.includes(status)
      )

      if (hasInvalidStatus) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('delivery.alert.hasInvalidStatusForFleetTask'),
              },
            ],
          },
        })
        return false
      }

      return true
    },
    async openCreateFoModal(modalId) {
      if (!this.validateCreateFoSelection()) return

      if (this.hasAccessAll(['tms-cold-chain:read'])) {
        const ids = this.selectedUpdateIds.map((item) => item.id)

        const dataTemp = await Delivery.validateDo({
          validation_type: 'HAS_TEMPERATURE_GAP',
          ids: ids,
        })

        if (dataTemp.errors) {
          this.$mcToast.error(this.$t('delivery.alert.requestError'))
          return
        }

        const getTemp = dataTemp?.data

        this.dataGapTemprerature = getTemp

        const savedPref = localStorage.getItem('hideTemperatureSummary')

        if (savedPref) {
          this.dontShowAgain = JSON.parse(savedPref)
        }

        if (
          !this.skipCheckTemp &&
          !this.dontShowAgain &&
          getTemp.min !== null &&
          getTemp.max !== null
        ) {
          this.pendingCreateFoModal = modalId
          this.dataConfirmTemp = getTemp
          this.$bvModal.show('modal-confirm-summary-temperature-attach')
          return
        }
      }

      this.$bvModal.show(modalId)
    },
    createFoDirect() {
      this.openCreateFoModal('modal-schedule-fleet-task-bulk')
    },
    createFoTransit() {
      this.openCreateFoModal('modal-schedule-fleet-task-bulk-transit')
    },
    handleApplyCreateFO() {
      this.skipCheckTemp = true
      this.$bvModal.show(this.pendingCreateFoModal || 'modal-schedule-fleet-task-bulk')
    },
    handleCancelScheduleFleetTaskBulk() {
      this.skipCheckTemp = false
      this.pendingCreateFoModal = null
      this.$bvModal.hide('modal-schedule-fleet-task-bulk')
      this.$bvModal.hide('modal-schedule-fleet-task-bulk-transit')
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-delivery {
  .mc-list-header {
    margin-bottom: 24px;
  }

  .delivery-header-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .delivery-header-actions--mobile {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }

  .delivery-table-toolbar__row {
    gap: 12px;
  }

  .delivery-table-toolbar__actions {
    flex-wrap: wrap;
  }

  :deep(.__button-group) {
    .mc-button {
      border-radius: 4px 0 0 4px;
      height: 100%;
    }
  }

  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }

  :deep(.btn) {
    display: flex;
    align-items: center;
    gap: 8px;

    &.btn-outline-primary {
      &:hover,
      &:focus,
      &:active {
        & > .mc-icon:before {
          color: #fff;
        }
      }

      & > .mc-icon:before {
        font-size: 16px;
      }
    }

    &.btn-primary {
      & > .mc-icon:before {
        color: #fff;
      }
    }
  }

  :deep(.mc-buttons-dropdown) {
    .icon-provider {
      width: 20px;
    }
  }

  :deep(.mc-border) {
    width: 2px;
    background: $white;
  }

  @media screen and (max-width: 767px) {
    .mc-list-header {
      margin-bottom: 16px;
    }

    :deep(.banner-header) {
      padding: 0px;
    }

    .mc-list-header--sticky {
      position: sticky;
      top: 70px;
      z-index: 116;
      width: auto;
      margin-left: -18px;
      margin-right: -18px;
      box-sizing: border-box;

      :deep(.banner-header) {
        margin: 0;
        padding: 12px 18px 16px;
        background: $white;
        border-bottom: 1px solid #edf2f7;
        box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
      }
    }

    :deep(.mc-table-footer) {
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    :deep(.mc-limit) {
      flex-wrap: nowrap;
      justify-content: center;
      gap: 6px;
      font-size: 12px;
    }

    :deep(.mc-limit .__limit),
    :deep(.mc-limit .__info) {
      justify-content: center;
      text-align: center;
      white-space: nowrap;
      width: auto;
      flex: 0 0 auto;
    }

    :deep(.mc-pagination-container) {
      justify-content: center;
    }

    :deep(.before-table > .mc-row-check) {
      display: none;
    }

    .delivery-table-toolbar__row {
      flex-wrap: wrap;
      align-items: center;
    }

    .delivery-table-toolbar__actions {
      flex: 0 0 auto;
      margin-left: auto;
      justify-content: flex-end;
    }

    :deep(.delivery-table-toolbar__inline-filter) {
      flex: 1 1 auto;
      min-width: 0;
      padding-bottom: 2px;
    }

    :deep(.slot-item-end) {
      width: 100%;
      justify-content: flex-start !important;
    }

    :deep(.delivery-table-toolbar__view-manager) {
      order: 2;
      flex: 0 0 auto;
    }

    :deep(.delivery-table-toolbar__view-manager .mc-button-wrapper) {
      width: auto;
    }

    :deep(.delivery-table-toolbar__view-manager .mc-button) {
      width: auto;
      justify-content: center;
      padding-left: 8px;
      padding-right: 8px;
    }

    :deep(.mc-has-search) {
      order: 1;
      flex: 1 1 0;
      min-width: 0;
    }

    .delivery-table-toolbar__search-actions {
      order: 3;
      flex: 0 0 auto;
    }

    .delivery-table-toolbar__selected-counter {
      order: 4;
      width: 100%;
      margin-bottom: 0;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 4px;
    }

    :deep(.delivery-table-toolbar__search-actions .mc-button) {
      padding-left: 8px;
      padding-right: 8px;
    }

    :deep(.delivery-header-actions__download),
    :deep(.delivery-header-actions__upload) {
      flex: 0 0 auto;
    }

    :deep(.delivery-header-actions__add-group) {
      display: flex;
      flex: 1 1 0;
      min-width: 0;
    }

    :deep(.delivery-header-actions__add-group > .mc-button-wrapper) {
      flex: 1 1 auto;
      min-width: 0;
    }

    :deep(.delivery-header-actions__add-group > .mc-button-wrapper .mc-button) {
      width: 100%;
      justify-content: center;
    }

    :deep(.delivery-header-actions__add-dropdown .btn) {
      height: 100%;
      padding-left: 12px;
      padding-right: 12px;
    }

    :deep(.delivery-header-actions__add-dropdown .dropdown-menu) {
      min-width: 240px;
    }
  }
}
</style>
<style lang="scss">
@import './index.scss';
</style>
